import { RxBarChart } from '@react-icons/all-files/rx/RxBarChart'
import { RxDownload } from '@react-icons/all-files/rx/RxDownload'
import { RxInput } from '@react-icons/all-files/rx/RxInput'
import { RxMixerHorizontal } from '@react-icons/all-files/rx/RxMixerHorizontal'
import { RxPlusCircled } from '@react-icons/all-files/rx/RxPlusCircled'
import { RxRows } from '@react-icons/all-files/rx/RxRows'
import { RxShare1 } from '@react-icons/all-files/rx/RxShare1'
import { RxTrash } from '@react-icons/all-files/rx/RxTrash'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { PHONE } from '@disco/constants/DeviceTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import {
  copyPlaylistToDiscoAndOpenInShortlists,
  copyToDisco,
} from '@disco/state-management/actions/ExplorerActions'
import {
  getPlaylistParams,
  loadPlaylistZipUrls,
} from '@disco/state-management/actions/playlist-actions'
import { removeLockedPlaylists } from '@disco/state-management/actions/ServerActions'
import { getTeamLibrary } from '@disco/state-management/selectors/groups'
import {
  getPlaylist,
  makeGetIfLockedPlaylistBelongsToRegularGroups,
  makeGetPlaylistRestrictions,
} from '@disco/state-management/selectors/playlists'
import { getUser } from '@disco/state-management/selectors/user'
import { isChannelShared } from '@disco/utils/groups'
import { downloadInFrameIfSupported } from '@disco/utils/target-iframe'
import { getZipDownloadsLinks } from '@disco/utils/tracks'

import type { Group } from 'types/group'
import type { PlaylistId } from 'types/playlist'

import { MixpanelEventLocationContext } from 'contexts/mixpanel-event-location'

import { Flex, useMenu } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

import AddToShortlist from './add-to-shortlist'
import EditPlaylist from './edit-playlist'
import PlaylistSettings from './playlist-settings'

type Props = {
  group: Group | null | undefined
  playlist: { playlist_id: PlaylistId } | null | undefined
  addToCustomGroup: boolean
  addToShortlist: boolean
  deletePlaylist: boolean
  downloadZip: boolean
  editMetadata: boolean
  editPlaylist: boolean
  playlistStats: boolean
  sharePlaylist: boolean
  trackOrder: boolean
  onPlaylistSettingsAwait: () => void
}

const PlaylistDropdown = ({
  group,
  playlist: playlistProp,
  addToCustomGroup,
  addToShortlist,
  deletePlaylist,
  downloadZip,
  editMetadata,
  editPlaylist,
  playlistStats,
  sharePlaylist,
  trackOrder,
  onPlaylistSettingsAwait,
}: Props): React.ReactNode => {
  const Menu = useMenu()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { featureContext } = React.useContext(MixpanelEventLocationContext)

  const getPlaylistRestrictions = React.useMemo(makeGetPlaylistRestrictions, [])
  const getIfLockedPlaylistBelongsToRegularGroups = React.useMemo(
    makeGetIfLockedPlaylistBelongsToRegularGroups,
    [],
  )

  const playlist = useSelector(state =>
    playlistProp ? getPlaylist(state, { id: playlistProp.playlist_id }) : null,
  )
  const user = useSelector(getUser)
  const restrictions = useSelector(state =>
    playlist
      ? getPlaylistRestrictions(state, { id: playlist.playlist_id })
      : null,
  )
  const displayRemoveFromBrowse = useSelector(state =>
    playlist
      ? getIfLockedPlaylistBelongsToRegularGroups(state, {
          id: playlist.playlist_id,
        })
      : false,
  )
  const isPhone = useSelector(state => state.app.mobileDeviceType === PHONE)
  const teamLibrary = useSelector(getTeamLibrary)

  React.useEffect(() => {
    if (!playlist) return
    if (
      user.business.private_metadata.territories_enabled &&
      playlist.has_any_territoryless_track === undefined
    ) {
      dispatch(getPlaylistParams(playlist))
    }
    if (!playlist.zip_urls) {
      dispatch(loadPlaylistZipUrls(playlist))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist?.playlist_id])

  if (!playlist) return null

  const { playlist_id } = playlist
  const groupId = group?.id
  const disableDelete = restrictions?.disableDelete ?? false

  const share = (() => {
    if (!sharePlaylist || !playlist.permissions?.share_link) return null
    const { has_any_territoryless_track } = playlist
    const territoriesEnabled =
      user.business.private_metadata.territories_enabled
    const link =
      territoriesEnabled &&
      (has_any_territoryless_track || user.territories.length > 1)
        ? `/explorer/playlists/${playlist_id}/share-warning`
        : `/explorer/playlists/${playlist_id}/share`
    const disabled =
      territoriesEnabled && has_any_territoryless_track === undefined
    return (
      <Menu.Item
        disabled={disabled}
        onSelect={() => {
          dispatch(sendMixpanelEvent('Share playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Share'),
            )
          }
          navigate(link)
        }}
      >
        <Flex align="center" gap="2">
          <RxShare1 />
          Share
        </Flex>
      </Menu.Item>
    )
  })()

  const stats = (() => {
    if (!playlistStats || !playlist.permissions?.manage) return null
    return (
      <Menu.Item
        onSelect={() => {
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Check stats'),
            )
          }
          dispatch(sendMixpanelEvent('Access playlist stats via playlist menu'))
          navigate(`/explorer/playlists/${playlist_id}/stats`)
        }}
      >
        <Flex align="center" gap="2">
          <RxBarChart />
          Stats
        </Flex>
      </Menu.Item>
    )
  })()

  const rename = (() => {
    if (!editMetadata || !playlist.permissions?.manage) return null
    return (
      <Menu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Rename playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent('Manage Artist modal - showcase - Rename'),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/metadata`)
        }}
      >
        <Flex align="center" gap="2">
          <RxInput />
          Rename
        </Flex>
      </Menu.Item>
    )
  })()

  const editAllMetadata = (() => {
    if (!playlist.permissions?.manage) return null
    return (
      <Menu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Open edit multiple via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Edit all track metadata',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/bulk-edit`)
        }}
      >
        <Flex align="center" gap="2">
          <RxMixerHorizontal />
          Edit all track metadata
        </Flex>
      </Menu.Item>
    )
  })()

  const editPlaylistItem = (() => {
    if (!editPlaylist || !playlist.permissions?.manage) return null
    return (
      <EditPlaylist
        key="edit-playlist"
        group={group}
        playlist={playlist}
        behavior="edit"
      />
    )
  })()

  const duplicatePlaylistItem = (() => {
    if (!editPlaylist || !playlist.permissions?.manage) return null
    return (
      <EditPlaylist
        key="duplicate-playlist"
        group={group}
        playlist={playlist}
        behavior="duplicate"
      />
    )
  })()

  const trackOrderItem = (() => {
    if (!trackOrder || !playlist.permissions?.manage) return null
    return (
      <Menu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Change track order via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Change track order',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/track-order`)
        }}
      >
        <Flex align="center" gap="2">
          <RxRows />
          Change track order
        </Flex>
      </Menu.Item>
    )
  })()

  const playlistSettingsItem = (() => {
    if (!playlist.permissions?.manage) return null
    return (
      <PlaylistSettings
        playlist={playlist}
        onNeedsAwait={onPlaylistSettingsAwait}
      />
    )
  })()

  const addToChannelItem = (() => {
    if (!addToCustomGroup || !playlist.permissions?.manage) return null
    return (
      <Menu.Item
        onSelect={() => {
          dispatch(sendMixpanelEvent('Add to channel via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Add to channel',
              ),
            )
          }
          navigate(
            `/explorer/playlists/${playlist_id}/add-to-channel${
              groupId ? `?source_group=${groupId}` : ''
            }`,
          )
        }}
      >
        <Flex align="center" gap="2">
          <RxPlusCircled />
          Add to channel
        </Flex>
      </Menu.Item>
    )
  })()

  const addToShortlistItem = (() => {
    if (!isPhone || !addToShortlist || !playlist.permissions?.manage)
      return null
    return (
      <AddToShortlist
        playlist={playlist}
        sourceGroupId={group?.id ?? null}
        sourceGroupType={group?.group_type ?? null}
      />
    )
  })()

  const copyToItem = (() => {
    if (playlist.permissions?.manage || !groupId) return null
    return (
      <Menu.Sub>
        <Menu.SubTrigger>Copy to</Menu.SubTrigger>
        <Menu.SubContent>
          <Menu.Item
            onSelect={() => {
              const teamLibraryId = teamLibrary?.id
              const objectUrl = playlist.view_playlist_url
              if (!teamLibraryId) return
              dispatch(
                copyToDisco(
                  playlist.name,
                  objectUrl,
                  { groupIds: [teamLibraryId] },
                  groupId,
                ),
              )
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Browse',
                Copy: 'Browse',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
            }}
          >
            Browse
          </Menu.Item>
          <Menu.Item
            onSelect={() => {
              navigate(
                `/explorer/playlists/${playlist_id}/${groupId}/copy-to-channel`,
              )
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Channel',
                Copy: 'Channel',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
            }}
          >
            Channel
          </Menu.Item>
          <Menu.Item
            onSelect={() => {
              mixpanel.sendPureEvent('Copy Shared Item', {
                Context: 'Shared Channels',
                Playlist_Name: playlist.name,
                Location: 'Shared Channel - Track Action Menu',
                Copy_To_Location: 'Playlist Creator',
                Copy: 'Playlist Creator',
                Type: 'Playlist',
                'Channel ID': groupId,
                'Channel Owner': group?.business?.id,
              })
              dispatch(copyPlaylistToDiscoAndOpenInShortlists(playlist))
            }}
          >
            Playlist Creator
          </Menu.Item>
        </Menu.SubContent>
      </Menu.Sub>
    )
  })()

  const downloadZipItem = (() => {
    if (!downloadZip) return null
    const links = getZipDownloadsLinks(playlist.zip_urls, 'inapp').map(
      ({ title, url }, index) => (
        <Menu.Item
          key={`download-${index}`}
          onSelect={() => {
            dispatch(
              sendMixpanelEvent(
                `Download ${title.toLowerCase()} via playlist menu`,
                {
                  ...(isChannelShared(group ?? undefined)
                    ? { Context: 'Shared Channels' }
                    : {}),
                },
              ),
            )
            if (featureContext === 'showcases') {
              dispatch(
                sendMixpanelEvent(
                  'Manage Artist modal - showcase - Download playlist',
                ),
              )
            }
            const a = document.createElement('a')
            a.href = url
            a.download = ''
            const target = downloadInFrameIfSupported()
            if (target) a.target = target
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
          }}
        >
          {title}
        </Menu.Item>
      ),
    )
    if (links.length === 0) return null
    return (
      <Menu.Sub>
        <Menu.SubTrigger>
          <Flex align="center" gap="2">
            <RxDownload />
            Download playlist
          </Flex>
        </Menu.SubTrigger>
        <Menu.SubContent>{links}</Menu.SubContent>
      </Menu.Sub>
    )
  })()

  const removeFromBrowseItem = (() => {
    if (!displayRemoveFromBrowse) return null
    return (
      <Menu.Item
        color="red"
        onSelect={() => {
          dispatch(removeLockedPlaylists([playlist]))
        }}
      >
        Remove from Browse
      </Menu.Item>
    )
  })()

  const deletePlaylistItem = (() => {
    if (!deletePlaylist || !playlist.permissions?.delete) return null
    return (
      <Menu.Item
        color="red"
        disabled={disableDelete}
        onSelect={() => {
          dispatch(sendMixpanelEvent('Delete playlist via playlist menu'))
          if (featureContext === 'showcases') {
            dispatch(
              sendMixpanelEvent(
                'Manage Artist modal - showcase - Delete playlist',
              ),
            )
          }
          navigate(`/explorer/playlists/${playlist_id}/delete`)
        }}
      >
        <Flex align="center" gap="2">
          <RxTrash />
          Delete playlist
        </Flex>
      </Menu.Item>
    )
  })()

  const editItems = [
    rename,
    editAllMetadata,
    editPlaylistItem,
    duplicatePlaylistItem,
    trackOrderItem,
  ].filter(Boolean)
  const actionItems = [
    addToChannelItem,
    addToShortlistItem,
    copyToItem,
    downloadZipItem,
    playlistSettingsItem,
  ].filter(Boolean)
  const dangerItems = [removeFromBrowseItem, deletePlaylistItem].filter(Boolean)

  return (
    <>
      {share}
      {stats}

      {editItems.length > 0 && (
        <>
          <Menu.Separator />
          <Menu.Label>Edit</Menu.Label>
          {editItems}
        </>
      )}

      {actionItems.length > 0 && (
        <>
          <Menu.Separator />
          <Menu.Label>Actions</Menu.Label>
          {actionItems}
        </>
      )}

      {dangerItems.length > 0 && (
        <>
          <Menu.Separator />
          {dangerItems}
        </>
      )}
    </>
  )
}

export default PlaylistDropdown
